import { useCallback, useState } from "react";
import { CircularProgress, IconButton, Tooltip } from "@material-ui/core";
import { NoteAdd, StickyNote2 } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { QuickNotesPopover, QUICK_NOTES_TRIGGER_PROP } from "components/_dashboard/rentalToSale/QuickNotesPopover";
import { useListingNotes } from "hooks/useListingNotes";

type Props = {
  listingId: string;
  /**
   * Whether this listing already has notes (drives the icon variant). On the
   * board this comes from the batched `useListingsWithNotes` set so 200 rows
   * don't each open a listener. Omit it together with `alwaysListen` on
   * single-listing surfaces to have the icon self-derive from the live notes.
   */
  hasNotes?: boolean;
  /**
   * Mount the notes listener immediately rather than only while the popover is
   * open, so the icon reflects whether notes exist without relying on the
   * batched `hasNotes` set. Safe on single-listing surfaces (e.g. the listing
   * details dialog) where there's no 200-listener concern.
   */
  alwaysListen?: boolean;
  /**
   * External loading signal (e.g. the batched `useListingsWithNotes` set is
   * still resolving). While loading and the notes state isn't known yet, the
   * trigger shows a spinner instead of the muted "add note" icon so an
   * existing-but-not-yet-loaded note doesn't look like "no notes".
   */
  loading?: boolean;
};

/**
 * Notes trigger that lives inline next to the listing address.
 * - hasNotes → solid green sticky-note icon
 * - no notes → muted "add note" icon
 *
 * Opens the popover on click; closes on click-away (handled by
 * `QuickNotesPopover`'s internal `useOutsideDismiss`, which also swallows
 * that click so it cannot reach the row underneath). The per-listing
 * onSnapshot only mounts once the popover actually opens (unless
 * `alwaysListen` is set), so 200 rows don't open 200 listeners on page load.
 */
export function ListingNotesPopover({
  listingId,
  hasNotes,
  alwaysListen = false,
  loading = false
}: Props) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const isOpen = anchor !== null;

  const {
    notes,
    hasNotes: derivedHasNotes,
    isLoading,
    addNote,
    updateNote,
    deleteNote,
    setNoteOrgShare
  } = useListingNotes(listingId, alwaysListen || isOpen);

  const showHasNotes = hasNotes ?? derivedHasNotes ?? false;
  // Spinner while we don't yet know the notes state: the external batched set
  // is still resolving, or (in alwaysListen mode) our own listener is loading.
  // Suppressed once we know there ARE notes, so the green icon wins.
  const showLoading = !showHasNotes && (loading || (alwaysListen && isLoading));

  const handleClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchor((prev) => (prev ? null : e.currentTarget));
  }, []);

  return (
    <>
      <Tooltip title={t("listingsBoard.notes.title")}>
        {/* Hands a tap to this trigger while ANOTHER card's notes are open, instead of
            letting that popover's dismissal eat it — see `QuickNotesPopover`. */}
        <IconButton size="small" onClick={handleClick} sx={{ p: 0.25 }} {...QUICK_NOTES_TRIGGER_PROP}>
          {showLoading ? (
            <CircularProgress size={16} />
          ) : showHasNotes ? (
            <StickyNote2 sx={{ fontSize: 16, color: "success.main" }} />
          ) : (
            <NoteAdd sx={{ fontSize: 16, color: "text.disabled" }} />
          )}
        </IconButton>
      </Tooltip>

      <QuickNotesPopover
        anchorEl={anchor}
        notes={notes}
        isLoading={isLoading}
        onClose={() => setAnchor(null)}
        onAddNote={addNote}
        onUpdateNote={updateNote}
        onDeleteNote={deleteNote}
        onSetNoteOrgShare={setNoteOrgShare}
      />
    </>
  );
}
